import React, { useEffect, useRef, useState } from "react";
import ChatMessage from "./components/ChatMessage";
// import AttachIcon from "./components/AttachIcon";
import SendIcon from "./components/SendIcon";
import { useClientChat } from "./hooks/useClientChat";
import chatBackgroundUrl from "../assets/chatbackground.png";
import styles from "./ClientChatPage.module.css";

export interface ClientChatPageProps {
  /** UUID клиента из маршрута /chat/:uuid */
  uuid: string;
}

const ClientChatPage: React.FC<ClientChatPageProps> = ({ uuid }) => {
  const {
    messages,
    messageText,
    setMessageText,
    isReady,
    isSending,
    isConnecting,
    error,
    sendMessage,
  } = useClientChat(uuid);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesScrollRef = useRef<HTMLDivElement>(null);
  const messagesContentRef = useRef<HTMLDivElement>(null);
  const messageInputRef = useRef<HTMLTextAreaElement>(null);
  const initialScrollTimeoutRef = useRef<number | null>(null);
  const keyboardBaseViewportBottomRef = useRef(0);
  const previousKeyboardInsetRef = useRef(0);
  const [keyboardInset, setKeyboardInset] = useState(0);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isInitialHistoryLoading, setIsInitialHistoryLoading] = useState(true);
  const [messagesTopSpacerHeight, setMessagesTopSpacerHeight] = useState(0);
  const isAppleMobile =
    typeof navigator !== "undefined" &&
    /iPhone|iPad|iPod/i.test(navigator.userAgent || "") &&
    typeof window !== "undefined" &&
    typeof window.visualViewport !== "undefined";

  useEffect(() => {
    setIsInitialHistoryLoading(true);
    if (initialScrollTimeoutRef.current !== null) {
      window.clearTimeout(initialScrollTimeoutRef.current);
      initialScrollTimeoutRef.current = null;
    }
  }, [uuid]);

  const scrollMessagesToBottom = (behavior: ScrollBehavior = "auto") => {
    const scroller = messagesScrollRef.current;
    if (!scroller) return;
    scroller.scrollTo({ top: scroller.scrollHeight, behavior });
  };

  useEffect(() => {
    const scroller = messagesScrollRef.current;
    const content = messagesContentRef.current;
    if (!scroller || !content) return;

    const recalculateTopSpacer = () => {
      const contentHeight = Math.ceil(content.getBoundingClientRect().height);
      const freeSpace = Math.max(0, scroller.clientHeight - contentHeight);
      setMessagesTopSpacerHeight((prev) => (Math.abs(prev - freeSpace) > 1 ? freeSpace : prev));
    };

    recalculateTopSpacer();

    window.addEventListener("resize", recalculateTopSpacer);
    window.addEventListener("orientationchange", recalculateTopSpacer);

    return () => {
      window.removeEventListener("resize", recalculateTopSpacer);
      window.removeEventListener("orientationchange", recalculateTopSpacer);
    };
  }, [isInitialHistoryLoading, keyboardInset, messages.length, messageText]);

  useEffect(() => {
    if (messages.length === 0) return;

    if (isInitialHistoryLoading) {
      scrollMessagesToBottom("auto");

      if (initialScrollTimeoutRef.current !== null) {
        window.clearTimeout(initialScrollTimeoutRef.current);
      }

      // Wait briefly until batched history updates settle, then hide loader.
      initialScrollTimeoutRef.current = window.setTimeout(() => {
        scrollMessagesToBottom("auto");
        setIsInitialHistoryLoading(false);
        initialScrollTimeoutRef.current = null;
      }, 120);
      return;
    }

    scrollMessagesToBottom("smooth");
  }, [isInitialHistoryLoading, messages.length]);

  useEffect(
    () => () => {
      if (initialScrollTimeoutRef.current !== null) {
        window.clearTimeout(initialScrollTimeoutRef.current);
      }
    },
    []
  );

  useEffect(() => {
    const textarea = messageInputRef.current;
    if (!textarea) return;

    textarea.style.height = "auto";

    const computedStyle = window.getComputedStyle(textarea);
    const lineHeight = Number.parseFloat(computedStyle.lineHeight || "20") || 20;
    const paddingTop = Number.parseFloat(computedStyle.paddingTop || "0") || 0;
    const paddingBottom = Number.parseFloat(computedStyle.paddingBottom || "0") || 0;
    const maxHeight = Math.round(lineHeight * 3 + paddingTop + paddingBottom);

    const nextHeight = Math.min(textarea.scrollHeight, maxHeight);
    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? "auto" : "hidden";
  }, [messageText]);

  useEffect(() => {
    const viewportMeta = document.querySelector('meta[name="viewport"]');
    const previousViewportContent = viewportMeta?.getAttribute("content") ?? null;

    // In the mobile app webview chat should stay fixed in its viewport area.
    if (viewportMeta) {
      viewportMeta.setAttribute(
        "content",
        "width=device-width,initial-scale=1,maximum-scale=1,user-scalable=no,viewport-fit=cover"
      );
    }

    let lastTouchEnd = 0;
    const preventGesture = (event: Event) => {
      event.preventDefault();
    };
    const preventPinchZoom = (event: TouchEvent) => {
      if (event.touches.length > 1) {
        event.preventDefault();
      }
    };
    const preventDoubleTapZoom = (event: TouchEvent) => {
      const now = Date.now();
      if (now - lastTouchEnd <= 300) {
        event.preventDefault();
      }
      lastTouchEnd = now;
    };

    document.addEventListener("gesturestart", preventGesture, { passive: false });
    document.addEventListener("gesturechange", preventGesture, { passive: false });
    document.addEventListener("gestureend", preventGesture, { passive: false });
    document.addEventListener("touchstart", preventPinchZoom, { passive: false });
    document.addEventListener("touchend", preventDoubleTapZoom, { passive: false });

    return () => {
      if (viewportMeta) {
        if (previousViewportContent !== null) {
          viewportMeta.setAttribute("content", previousViewportContent);
        } else {
          viewportMeta.removeAttribute("content");
        }
      }
      document.removeEventListener("gesturestart", preventGesture);
      document.removeEventListener("gesturechange", preventGesture);
      document.removeEventListener("gestureend", preventGesture);
      document.removeEventListener("touchstart", preventPinchZoom);
      document.removeEventListener("touchend", preventDoubleTapZoom);
    };
  }, []);

  useEffect(() => {
    if (!isInputFocused || messages.length === 0) {
      previousKeyboardInsetRef.current = keyboardInset;
      return;
    }

    const previousInset = previousKeyboardInsetRef.current;
    previousKeyboardInsetRef.current = keyboardInset;

    // Compensate only when keyboard opens/grows, not on every small viewport tick.
    if (keyboardInset > previousInset + 4) {
      window.requestAnimationFrame(() => scrollMessagesToBottom("auto"));
    }
  }, [isInputFocused, keyboardInset, messages.length]);

  useEffect(() => {
    if (!isAppleMobile) {
      setKeyboardInset(0);
      return;
    }

    const viewport = window.visualViewport;
    if (!viewport) return;

    const getViewportBottom = () => Math.round(viewport.height + viewport.offsetTop);
    const updateKeyboardInset = () => {
      const viewportBottom = getViewportBottom();

      if (!isInputFocused) {
        keyboardBaseViewportBottomRef.current = Math.max(
          keyboardBaseViewportBottomRef.current,
          viewportBottom
        );
        setKeyboardInset(0);
        return;
      }

      if (keyboardBaseViewportBottomRef.current === 0) {
        keyboardBaseViewportBottomRef.current = viewportBottom;
      }

      const inset = Math.max(0, keyboardBaseViewportBottomRef.current - viewportBottom);
      setKeyboardInset(inset > 0 ? inset : 0);
    };

    updateKeyboardInset();
    viewport.addEventListener("resize", updateKeyboardInset);
    viewport.addEventListener("scroll", updateKeyboardInset);
    window.addEventListener("orientationchange", updateKeyboardInset);

    return () => {
      viewport.removeEventListener("resize", updateKeyboardInset);
      viewport.removeEventListener("scroll", updateKeyboardInset);
      window.removeEventListener("orientationchange", updateKeyboardInset);
    };
  }, [isAppleMobile, isInputFocused]);

  const canSend = isReady && !isSending && messageText.trim().length > 0;
  const showErrorOverlay = Boolean(error);
  const showCenteredOverlay =
    showErrorOverlay || (isConnecting && messages.length === 0);
  const showEmptyState = messages.length === 0 && !isConnecting && !error;
  const shouldRenderMessages = !showErrorOverlay;
  const showInitialLoader = messages.length > 0 && isInitialHistoryLoading;

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    sendMessage();
  };

  const onInputKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const isTouchLikeDevice =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(pointer: coarse)").matches;

    // On mobile keyboards Enter should create a new line.
    if (isTouchLikeDevice) return;

    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (canSend) sendMessage();
    }
  };

  return (
    <div className={styles.page}>
      <main className={styles.main}>
        <div
          className={styles.messagesArea}
          style={{ backgroundImage: `url(${chatBackgroundUrl})` }}
        >
          <div className={styles.messagesScroll} ref={messagesScrollRef}>
            <div
              className={styles.messagesTopSpacer}
              style={
                messagesTopSpacerHeight > 0
                  ? { height: `${messagesTopSpacerHeight}px` }
                  : undefined
              }
              aria-hidden="true"
            />
            <div ref={messagesContentRef}>
              {shouldRenderMessages
                ? messages.map((message) => (
                    <ChatMessage
                      key={message.id}
                      text={message.text}
                      sender={message.sender}
                      timeLabel={message.timeLabel}
                      isOutgoing={message.isOutgoing}
                    />
                  ))
                : null}
              <div
                ref={messagesEndRef}
                className={styles.messagesBottomAnchor}
                aria-hidden="true"
              />
            </div>
          </div>

          {showCenteredOverlay ? (
            <div className={styles.centerOverlay}>
              {error ? (
                <p className={styles.serviceNotice}>{error}</p>
              ) : isConnecting ? (
                <p className={styles.state}>Подключение…</p>
              ) : null}
            </div>
          ) : null}

          {showEmptyState ? (
            <div className={styles.centerOverlay}>
              <p className={styles.state}>Сообщений пока нет</p>
            </div>
          ) : null}

          {showInitialLoader ? (
            <div className={styles.centerOverlay}>
              <div className={styles.loadingBox}>
                <span className={styles.loadingSpinner} aria-hidden="true" />
                <span className={styles.loadingText}>Загрузка сообщений…</span>
              </div>
            </div>
          ) : null}
        </div>

        <form
          className={styles.sendForm}
          onSubmit={onSubmit}
          style={
            isAppleMobile && keyboardInset > 0
              ? { marginBottom: `${keyboardInset}px` }
              : undefined
          }
        >
          <div className={styles.sendPrimaryRow}>
            {/* <button
              type="button"
              className={styles.attachButton}
              disabled={!isReady}
              aria-label="Прикрепить файл"
              title="Прикрепить файл"
            >
              <AttachIcon />
            </button> */}
            <textarea
              ref={messageInputRef}
              className={styles.sendInput}
              value={messageText}
              onChange={(event) => setMessageText(event.target.value)}
              onKeyDown={onInputKeyDown}
              onFocus={() => setIsInputFocused(true)}
              onBlur={() => {
                setIsInputFocused(false);
                setKeyboardInset(0);
                // iOS can lag visualViewport updates after blur; enforce reset.
                window.setTimeout(() => setKeyboardInset(0), 220);
              }}
              placeholder="Текст сообщения"
              disabled={!isReady || isSending}
              rows={1}
            />
            <button
              type="submit"
              className={styles.sendButton}
              disabled={!canSend}
              aria-label="Отправить"
              title="Отправить"
            >
              <SendIcon />
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default ClientChatPage;
